const HEADER_HINTS = [
  'unit',
  'resident',
  'tenant',
  'name',
  'rent',
  'balance',
  'deposit',
  'phone',
  'email',
  'move',
  'lease',
  'expiration',
  'amount',
  'market',
  'actual',
  'sq ft',
];

const METADATA_PATTERNS = [
  'as of',
  'month year',
  'report',
  'summary',
  'current/notice/vacant',
];

function normalizeCell(value) {
  return String(value ?? '')
    .replace(/^\uFEFF/, '')
    .replace(/\r/g, '')
    .trim();
}

function trimTrailingEmptyCells(row = []) {
  const nextRow = [...row];
  while (nextRow.length > 0 && !normalizeCell(nextRow[nextRow.length - 1])) {
    nextRow.pop();
  }
  return nextRow;
}

function countNonEmpty(cells = []) {
  return cells.filter(Boolean).length;
}

function isLikelyNumber(value) {
  const normalized = normalizeCell(value).replace(/[$,%\s]/g, '').replace(/,/g, '');
  if (!normalized) {
    return false;
  }

  return /^-?\d+(\.\d+)?$/.test(normalized);
}

function isLikelyDate(value) {
  const normalized = normalizeCell(value);
  if (!normalized) {
    return false;
  }

  return /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(normalized);
}

function scoreHeaderRow(cells = []) {
  const nonEmptyCount = countNonEmpty(cells);
  if (nonEmptyCount < 2) {
    return Number.NEGATIVE_INFINITY;
  }

  const joined = cells.join(' ').toLowerCase();
  let score = nonEmptyCount >= 4 ? 5 : 2;

  if (METADATA_PATTERNS.some((pattern) => joined.includes(pattern))) {
    score -= 6;
  }

  if (joined.includes('=')) {
    score -= 3;
  }

  cells.forEach((cell) => {
    const lower = cell.toLowerCase();
    if (HEADER_HINTS.some((hint) => lower.includes(hint))) {
      score += 2;
    }

    if (isLikelyNumber(cell) || isLikelyDate(cell)) {
      score -= 1;
    } else {
      score += 0.35;
    }
  });

  return score;
}

function isContinuationHeaderRow(cells = []) {
  const nonEmptyCount = countNonEmpty(cells);
  if (nonEmptyCount === 0 || nonEmptyCount === 1) {
    return false;
  }

  if (cells.some((cell) => isLikelyNumber(cell) || isLikelyDate(cell))) {
    return false;
  }

  return nonEmptyCount <= Math.max(6, Math.ceil(cells.length * 0.65));
}

function mergeHeaderCells(primaryCells = [], continuationCells = []) {
  const maxLength = Math.max(primaryCells.length, continuationCells.length);
  const merged = [];

  for (let index = 0; index < maxLength; index += 1) {
    const primary = normalizeCell(primaryCells[index]);
    const continuation = normalizeCell(continuationCells[index]);

    if (primary && continuation && !primary.toLowerCase().includes(continuation.toLowerCase())) {
      merged.push(`${primary} ${continuation}`.trim());
      continue;
    }

    merged.push(primary || continuation || `Column ${index + 1}`);
  }

  return merged;
}

function dedupeHeaders(headers = []) {
  const seen = new Map();

  return headers.map((header, index) => {
    const base = normalizeCell(header) || `Column ${index + 1}`;
    const key = base.toLowerCase();
    const nextCount = (seen.get(key) || 0) + 1;
    seen.set(key, nextCount);
    return nextCount === 1 ? base : `${base} ${nextCount}`;
  });
}

function isIgnorableRow(cells = []) {
  const nonEmptyCount = countNonEmpty(cells);
  if (nonEmptyCount === 0 || nonEmptyCount === 1) {
    return true;
  }

  const joined = cells.join(' ').toLowerCase();
  if (joined.includes('current/notice/vacant')) {
    return true;
  }

  if (/^(total|subtotal|grand total)\b/.test(joined)) {
    return true;
  }

  return false;
}

function buildObjects(headers = [], rawRows = []) {
  return rawRows
    .map((rawRow) => trimTrailingEmptyCells((rawRow || []).map(normalizeCell)))
    .filter((cells) => !isIgnorableRow(cells))
    .map((cells) => {
      const record = {};
      headers.forEach((header, index) => {
        record[header] = normalizeCell(cells[index]);
      });
      return record;
    })
    .filter((record) => Object.values(record).some(Boolean));
}

export function extractTabularData(rawMatrix = []) {
  const matrix = rawMatrix
    .filter((row) => Array.isArray(row))
    .map((row) => trimTrailingEmptyCells(row.map(normalizeCell)));

  if (matrix.length === 0) {
    return {
      headers: [],
      rows: [],
      headerRowIndex: -1,
      continuationHeaderUsed: false,
    };
  }

  let bestHeaderIndex = 0;
  let bestScore = Number.NEGATIVE_INFINITY;

  matrix.slice(0, Math.min(matrix.length, 15)).forEach((row, index) => {
    const score = scoreHeaderRow(row);
    if (score > bestScore) {
      bestScore = score;
      bestHeaderIndex = index;
    }
  });

  const primaryHeaderRow = matrix[bestHeaderIndex] || [];
  const secondaryHeaderRow = matrix[bestHeaderIndex + 1] || [];
  const continuationHeaderUsed = isContinuationHeaderRow(secondaryHeaderRow);
  const headers = dedupeHeaders(
    mergeHeaderCells(primaryHeaderRow, continuationHeaderUsed ? secondaryHeaderRow : [])
  );
  const dataStartIndex = bestHeaderIndex + 1 + (continuationHeaderUsed ? 1 : 0);
  const rows = buildObjects(headers, matrix.slice(dataStartIndex));

  return {
    headers,
    rows,
    headerRowIndex: bestHeaderIndex,
    continuationHeaderUsed,
  };
}
